import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router';
import useWizardStore from '../stores/useWizardStore';
import { StudyEyeSelector } from './StudyEyeSelector';
import { MainContext } from '@/rewrite/contexts/MainContext';
import type {
  GetParticipantStudyEyeResponse,
  StudyEyeValue,
} from '../types/studyEvents';

const STUDY_EYE_LABEL: Record<string, string> = {
  OD: 'OD (Right)',
  OS: 'OS (Left)',
  NA: 'Not Available',
};
const labelStudyEye = (value?: StudyEyeValue | string | null): string =>
  value ? (STUDY_EYE_LABEL[value] ?? value) : '—';

const EVENT_TYPE_LABEL: Record<string, string> = {
  'study_eye.reported': 'reported at upload',
  'study_eye.confirmed': 'confirmed',
  'study_eye.changed': 'changed',
  'study_eye.marked_not_available': 'marked not available',
  'study_eye.conflict_observed': 'conflict',
  'study_eye.conflict_resolved': 'conflict resolved',
};
const prettyEventType = (type?: string | null): string =>
  type ? (EVENT_TYPE_LABEL[type] ?? type.replace(/^study_eye\./, '').replace(/_/g, ' ')) : 'event';

// recorded_at / occurred_at come from the backend as UTC "YYYY-MM-DD HH:MM:SS"
// (PHP gmdate). Render in Eastern Time with an EST/EDT label, matching the
// dashboard's dateUtils convention.
const formatEastern = (dbUtc?: string | null): string => {
  if (!dbUtc) return '';
  const iso = dbUtc.includes('T') ? dbUtc : dbUtc.replace(' ', 'T');
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return dbUtc;
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/New_York', timeZoneName: 'short',
  });
};

export const StartPage = ({
  onStart,
}): JSX.Element => {
  const navigate = useNavigate();
  const { imageSyncService } = useContext(MainContext)!;
  const cacheLoaded = useWizardStore(state => state.cacheLoaded);
  const hasRequiredImages = useWizardStore(state => state.hasRequiredImages);
  const selectedCaptureDate = useWizardStore(state => state.selectedCaptureDate);
  const studyEye = useWizardStore(state => state.studyEye);
  const setStudyEye = useWizardStore(state => state.setStudyEye);

  const [recorded, setRecorded] = useState<GetParticipantStudyEyeResponse | null>(null);

  useEffect(() => {
    let active = true;
    imageSyncService.getParticipantStudyEye()
      .then(res => {
        if (!active) return;
        setRecorded(res);
        // Prefill from the latest current designation. The backend returns
        // `current` as the newest affects_current event (MAX by event id), so
        // this is always the most recent value. Only OD/OS is a design study
        // eye (NA is not), and never override a pick the designer already made.
        const current = res.current?.value;
        if ((current === 'OD' || current === 'OS') && !useWizardStore.getState().studyEye)
          setStudyEye(current);
      })
      .catch(() => {
        // Non-blocking: the designer can still choose manually.
        console.warn('Could not load the recorded Study Eye.');
      });
    return () => { active = false; };
  }, [imageSyncService, setStudyEye]);

  const handleManageImages = useCallback(() => {
    navigate('/', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasRequiredImages)
    return contain(<>
      <div>Images for this user are not ready on this device. Go to Image Management to synchronize files or import new files.</div>
      <button onClick={handleManageImages} className='btn'>Manage Images</button>
    </>);

  const canDesign = !!studyEye && cacheLoaded;
  const current = recorded?.current ?? null;
  const baseline = recorded?.layout_baseline ?? null;
  const conflicts = recorded?.open_conflicts ?? [];
  const history = recorded?.history ?? [];

  return contain(<>
    <div className='text-gray-700'>{`Images dated: ${selectedCaptureDate}`}</div>

    <div className='flex flex-row items-center gap-2'>
      <span className='font-medium text-gray-700'>Study Eye:</span>
      <StudyEyeSelector />
    </div>

    {recorded?.enabled && (
      <div className='w-full max-w-md rounded-lg border border-sky-200 bg-sky-50/60 p-3 text-left'>
        <div className='mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-sky-800'>
          Reading Center · Study Eye
        </div>

        {current ? (
          <div className='flex flex-col gap-1'>
            <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
              <span className='rounded-full bg-sky-600 px-2.5 py-0.5 text-sm font-semibold text-white'>
                {labelStudyEye(current.value)}
              </span>
              {current.value === 'NA' && current.reason && (
                <span className='text-sm text-gray-600'>{current.reason}</span>
              )}
            </div>
            <div className='text-xs text-gray-500'>
              Recorded {formatEastern(current.recorded_at)}
              {current.claimed_operator_name ? ` by ${current.claimed_operator_name}` : ''}
            </div>
          </div>
        ) : (
          <div className='text-sm text-gray-500'>No Study Eye recorded yet by the Reading Center.</div>
        )}

        {baseline?.value && (
          <div className='mt-2 text-xs text-gray-600'>
            Existing layout designation: <span className='font-medium text-gray-700'>{labelStudyEye(baseline.value)}</span>
            {baseline.internally_consistent ? '' : ' — layouts inconsistent'}
          </div>
        )}

        {conflicts.length > 0 && (
          <div className='mt-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900'>
            <div className='font-semibold'>Unresolved conflict{conflicts.length > 1 ? 's' : ''}</div>
            {conflicts.map(c => (
              <div key={c.event_id} className='mt-0.5'>
                {labelStudyEye(c.value)} <span className='text-amber-700'>· {formatEastern(c.recorded_at)}</span>
              </div>
            ))}
          </div>
        )}

        {history.length > 0 && (
          <details className='mt-2 text-xs'>
            <summary className='cursor-pointer select-none text-gray-500 hover:text-gray-700'>
              History ({history.length})
            </summary>
            <ul className='mt-1 flex flex-col gap-0.5'>
              {history.map(h => (
                <li key={h.event_id} className='flex flex-wrap items-baseline gap-x-1.5 text-gray-600'>
                  <span className='font-medium text-gray-700'>{labelStudyEye(h.value)}</span>
                  <span className='text-gray-400'>{prettyEventType(h.event_type)}</span>
                  <span className='text-gray-400'>· {formatEastern(h.recorded_at)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    )}

    <button onClick={onStart} disabled={!canDesign} className='btn'>Start Designing</button>
  </>);
}


function contain(children: React.ReactNode): JSX.Element {
  return (<div className='flex flex-col gap-3 justify-evenly items-center bg-gray-100 p-3 rounded-md min-h-[12rem]'>
    {children}
  </div>);
}
